export interface CachedPDF {
  userId: string;
  pdfBlob: Blob;
  markdownHash: string;
  cachedAt: Date;
}

// DAG-002 (Round 6 #104): exported profile PDFs were previously cached
// indefinitely in IndexedDB. Governed personal data should not live on
// a device forever — match the rest of the governed-cache TTL window
// (24h, same as the proposal scrape cache and IndexedDB-stored proposal
// history) and purge expired entries on init.
const PDF_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

class PDFCacheService {
  private dbName = 'PDFCacheDB';
  private dbVersion = 1;
  private storeName = 'pdfCache';
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Failed to open PDF cache IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        // DAG-002 startup purge: drop any entry older than the TTL
        // before any consumer reads the cache. Fire-and-forget — a
        // purge failure must not block init.
        this.purgeExpired().catch((err) => {
          console.warn('PDF cache purge-on-init failed:', err);
        });
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, {
            keyPath: 'userId'
          });

          objectStore.createIndex('markdownHash', 'markdownHash', { unique: false });
          objectStore.createIndex('cachedAt', 'cachedAt', { unique: false });
        }
      };
    });
  }

  /**
   * Delete cached entries older than PDF_CACHE_TTL_MS. Called on init
   * to enforce DAG-002 (#104) governed retention.
   */
  async purgeExpired(): Promise<number> {
    const db = await this.ensureDB();
    const cutoff = Date.now() - PDF_CACHE_TTL_MS;
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    const cachedAtIndex = store.index('cachedAt');

    return new Promise((resolve, reject) => {
      let deleted = 0;
      const range = IDBKeyRange.upperBound(new Date(cutoff));
      const cursorRequest = cachedAtIndex.openCursor(range);
      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null;
        if (cursor) {
          cursor.delete();
          deleted += 1;
          cursor.continue();
        } else {
          resolve(deleted);
        }
      };
      cursorRequest.onerror = () => reject(cursorRequest.error);
    });
  }

  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error('Failed to initialize PDF cache database');
    }
    return this.db;
  }

  /**
   * Calculate SHA-256 hash of markdown content
   */
  async calculateHash(markdown: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(markdown);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  /**
   * Cache a PDF with its corresponding markdown hash
   */
  async cachePDF(userId: string, pdfBlob: Blob, markdownHash: string): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const cachedPDF: CachedPDF = {
        userId,
        pdfBlob,
        markdownHash,
        cachedAt: new Date()
      };

      const request = store.put(cachedPDF);
      request.onsuccess = () => {
        console.log(`PDF cached for user ${userId} with hash ${markdownHash.substring(0, 8)}...`);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get cached PDF if the markdown hash matches
   */
  async getCachedPDF(userId: string, currentMarkdownHash: string): Promise<Blob | null> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.get(userId);
      request.onsuccess = () => {
        const cached = request.result as CachedPDF | undefined;
        if (!cached) {
          resolve(null);
          return;
        }
        // DAG-002 (#104): even a hash-match is a miss if the cached
        // entry is past TTL. The purge-on-init runs at startup, but a
        // long-lived tab might still hold a stale entry.
        const cachedAt = cached.cachedAt instanceof Date
          ? cached.cachedAt.getTime()
          : new Date(cached.cachedAt).getTime();
        if (Date.now() - cachedAt > PDF_CACHE_TTL_MS) {
          resolve(null);
          return;
        }
        if (cached.markdownHash === currentMarkdownHash) {
          resolve(cached.pdfBlob);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear cached PDF for a user
   */
  async clearCache(userId: string): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.delete(userId);
      request.onsuccess = () => {
        console.log(`PDF cache cleared for user ${userId}`);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all cached PDFs
   */
  async clearAllCache(): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => {
        console.log('All PDF cache cleared');
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }
}

export const pdfCacheService = new PDFCacheService();